<template>
  <div>
    <div v-for="(section, index) in splitContent" :key="index" class="page" :class="pageSizeClass">
      <!-- 페이지 헤더 -->
      <div class="page-common-header" v-if="index != 0">
        <div v-if="isEvenPage(index)" class="header-text-container left">
          <div>
            <span class="page-number"> {{ getPageNumber(index) }} </span>
          </div>
          <div v-if="item?.authors?.length > 0">
            <span class="header-text"> {{ displayAuthors }} </span>
          </div>
        </div>

        <div v-else class="header-text-container right">
          <div>
            <span class="header-text"> {{ displayTitle }} </span>
          </div>
          <div>
            <span class="page-number"> {{ getPageNumber(index) }} </span>
          </div>
        </div>
      </div>

      <!-- 페이지 타이틀 -->
      <div v-if="index === 0">
        <div class="title-container">
          <h4 class="page-title">
            {{ displayTitle }}
          </h4>
          <h5 class="page-subtitle" v-if="item.subtitle">
            {{ displaySubtitle }}
          </h5>
        </div>

        <div class="author-container" v-if="item.authors?.length > 0">
          <div class="author" v-if="displayAuthors">
            {{ displayAuthors }}
          </div>
          <div class="author-organization" v-if="displayAuthorOrganizations">
            {{ displayAuthorOrganizations }}
          </div>
        </div>
      </div>

      <!-- 페이지 컨텐츠 -->
      <div class="page-content" v-html="sanitizeHtml(section)"></div>

      <!-- 주석 영역 -->
      <div
        class="page-comments"
        :class="(index + startPageNumber) % 2 === 0 ? 'right' : 'left'"
      >
        <div
          v-for="comment in splitComments[index] || []"
          :key="comment.idx"
          class="page-comment"
        >
          <sup>{{ comment.superscript }}</sup> {{ comment.content }}
        </div>
      </div>
    </div>

    <!-- 템플릿 스타일 측정용 숨겨진 요소 -->
    <div
      ref="templateRef"
      style="
        visibility: hidden;
        position: absolute;
        top: -9999px;
        left: -9999px;
      "
    >
      <div class="title-container">
        <h4 class="page-title">
          {{ displayTitle }}
        </h4>
        <h5 class="page-subtitle" v-if="item.subtitle">
          {{ displaySubtitle }}
        </h5>
      </div>

      <div class="author-container" v-if="item?.authors?.length > 0">
        <div class="author" v-if="displayAuthors">
          {{ displayAuthors }}
        </div>
        <div class="author-organization" v-if="displayAuthorOrganizations">
          {{ displayAuthorOrganizations }}
        </div>
      </div>
      <div class="page-content" v-html="sanitizeHtml(item.content)"></div>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-console */
import { getSplitContentByHeight } from "@/components/Templates/heightCalculator";
import { sanitizeHtml } from "@/utils/sanitizeHtml";

export default {
  name: "proceedings-paper-page",
  props: {
    item: {
      type: Object,
      required: true,
      default: () => {},
    },
    group: {
      type: Object,
      required: false,
      default: () => {},
    },
    startPageNumber: {
      type: Number,
      required: true,
    },
    journal: {
      type: Object,
      required: true,
      default: () => {},
    },
    pageSize: {
      type: String,
      default: "A4",
    },
  },
  data() {
    return {
      splitContent: [],
      splitComments: [],
      templateRef: null,
    };
  },
  computed: {
    pageSizeClass() {
      return this.pageSize?.toLowerCase() || "a4";
    },
    pageSizeConfig() {
      const configs = {
        a4: { width: 794, height: 1122 },
        a5: { width: 559, height: 794 },
      };
      return configs[this.pageSizeClass] || configs.a4;
    },
    displayAuthors() {
      return `${
        this.item?.authors
          ? this.item.authors.map((author) => `${author.full_name}`).join(" • ")
          : ""
      }`;
    },
    displayAuthorOrganizations() {
      return this.item?.authors
        ? `| ${this.item.authors
            .map((author) => `${author.organization}`)
            .join(" • ")} |`
        : "";
    },
    displayTitle() {
      return this.item?.title;
    },
    displaySubtitle() {
      return `⎯ ${this.item?.subtitle} ⎯`;
    },
  },
  methods: {
    sanitizeHtml,
    getPageNumber(index) {
      return index + this.startPageNumber;
    },
    isEvenPage(index) {
      return this.getPageNumber(index) % 2 === 0;
    },
    splitContentByHeight(htmlString) {
      const { width: PAGE_WIDTH, height: PAGE_HEIGHT } = this.pageSizeConfig;
      const HORIZONTAL_PADDING = 123; //  (px, 32.5mm)
      const VERTICAL_PADDING = 75; // (px, 20mm)

      // 타이틀 영역 높이 계산
      const titleContainer =
        this.$refs.templateRef?.querySelector(".title-container");
      const authorContainer =
        this.$refs.templateRef?.querySelector(".author-container");
      const titleAreaHeight = titleContainer ? titleContainer.offsetHeight : 0;
      const authorAreaHeight = authorContainer
        ? authorContainer.offsetHeight
        : 0;
      const totalTitleAreaHeight = titleAreaHeight + authorAreaHeight + 20; // 20px는 여유 공간

      return getSplitContentByHeight({
        content: htmlString,
        pageWidth: PAGE_WIDTH,
        pageHeight: PAGE_HEIGHT,
        horizontalPadding: HORIZONTAL_PADDING,
        verticalPadding: VERTICAL_PADDING,
        templateRef: this.$refs.templateRef,
        titleAreaHeight: totalTitleAreaHeight,
      });
    },
    getCommentsForPage(pageNumber) {
      // 주어진 페이지 번호에 해당하는 주석을 필터링
      return this.item.comments.filter(
        (comment) => comment.page === pageNumber
      );
    },
    commentsByPages(pageComments, availableHeight) {
      const pages = [];
      let current = [];
      let currentHeight = 0;

      for (const comment of pageComments) {
        // 임시 DOM 만들어서 높이 측정 (또는 글자 수 기반 추정 가능)
        const tempDiv = document.createElement("div");
        tempDiv.style.position = "absolute";
        tempDiv.style.visibility = "hidden";
        tempDiv.style.width = "60px"; // 주석 폭에 맞춤
        tempDiv.style.fontSize = "10px";
        tempDiv.style.lineHeight = "13px";
        tempDiv.style.fontFamily =
          '"KoPubWorldDotum", "Noto Sans KR", sans-serif';
        tempDiv.innerHTML = `<sup>${comment.superscript}</sup> ${comment.content}`;

        document.body.appendChild(tempDiv);
        const height = tempDiv.offsetHeight;
        document.body.removeChild(tempDiv);

        if (currentHeight + height > availableHeight) {
          pages.push(current);
          current = [comment];
          currentHeight = height;
        } else {
          current.push(comment);
          currentHeight += height;
        }
      }

      if (current.length > 0) {
        pages.push(current);
      }

      return pages;
    },
    // 폰트 로딩 완료 대기 (일관된 높이 계산을 위해)
    async waitForFonts() {
      if (document.fonts && document.fonts.ready) {
        try {
          await document.fonts.ready;
        } catch (e) {
          console.warn('Font loading check failed:', e);
        }
      }
      return new Promise(resolve => setTimeout(resolve, 50));
    },
  },
  async mounted() {
    await this.waitForFonts();
    this.splitContent = this.splitContentByHeight(this.item?.content);
    this.splitComments = this.commentsByPages(this.item?.comments, this.pageSizeConfig.height - 123);
    this.$emit("page-count", this.splitContent.length);
  },
  watch: {
    "item.content": {
      immediate: true,
      async handler(newContent) {
        await this.waitForFonts();
        this.$nextTick(() => {
          this.splitContent = this.splitContentByHeight(newContent);
          // content가 변경되면 comments도 다시 계산
          this.splitComments = this.commentsByPages(
            this.item?.comments || [],
            this.pageSizeConfig.height - 123
          );
          this.$emit("page-count", this.splitContent.length);
        });
      },
    },
    "item.comments": {
      immediate: true,
      async handler(newComments) {
        await this.waitForFonts();
        this.$nextTick(() => {
          this.splitComments = this.commentsByPages(
            newComments || [],
            this.pageSizeConfig.height - 123
          );
        });
      },
    },
  },
};
</script>

<style lang="scss" scoped>
$primary-font-color: #000000;
$secondary-font-color: #8b8b8b;
$background-color: #cbcccb;

$a4-width: 794px;
$a4-height: 1122px;
$a5-width: 559px;
$a5-height: 794px;

$page-vertical-padding: 75px;
$page-horizontal-padding: 123px;

$dotum-font-family: "KoPubWorldDotum", "Noto Sans KR", sans-serif;
$batang-font-family: "KoPubWorldBatang", "Noto Sans KR", sans-serif;

/** ****************************************************************************************
 * [ 페이지 공통 스타일 ]
 *******************************************************************************************/
.page {
  height: auto;
  background-color: white;
  position: relative;
  box-sizing: border-box;
  padding: $page-vertical-padding $page-horizontal-padding;
  overflow: visible;
  border: 0.5mm solid #ddd; /* 연한 회색 테두리 */

  &.a4 {
    width: $a4-width;
    min-height: $a4-height;
  }

  &.a5 {
    width: $a5-width;
    min-height: $a5-height;
  }
}

.page-common-header {
  position: absolute;
  top: $page-vertical-padding - 30;
  left: $page-horizontal-padding;
  right: $page-horizontal-padding;
}

.header-text-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;

  &.right {
    justify-content: flex-end;
  }

  &.left {
    justify-content: flex-start;
  }
}

.header-text {
  font-size: 12px;
  font-weight: 600;
  color: $primary-font-color;
  font-family: $dotum-font-family;
}

.page-number {
  font-size: 12px;
  font-weight: 600;
  color: $primary-font-color;
  font-family: $batang-font-family;
}
/** ****************************************************************************************
* [ 페이지 스타일 ]
*******************************************************************************************/

.author-container {
  display: flex;
  flex-direction: column;
  align-items: end;
  justify-content: center;
  margin-top: 10px;
  margin-bottom: 10px;
}

.author {
  font-size: 12px;
  font-weight: 600;
  color: $primary-font-color;
  font-family: $dotum-font-family;
}

.author-organization {
  font-size: 11px;
  font-weight: 200;
  color: $primary-font-color;
  font-family: $dotum-font-family;
}

.title-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.page-title {
  font-size: 20px;
  line-height: 33px;
  font-weight: 400;
  text-align: center;
  color: $primary-font-color;
  font-family: $batang-font-family;
  padding: 0px 15px;
}

.page-subtitle {
  font-size: 18px;
  font-weight: 300;
  text-align: center;
  color: $primary-font-color;
  font-family: $batang-font-family;
}

/** ****************************************************************************************
* [ 페이지 스타일 ]
*******************************************************************************************/
$proceedings-content-p-font-size: 12.39px;
$proceedings-content-p-line-height: 22.24px;
$proceedings-content-p-letter-spacing: -0.47px;
$proceedings-content-p-transform: scaleX(0.97);
$proceedings-content-p-font-weight: 300;
$proceedings-content-p-word-break: break-all;
$proceedings-content-p-bottom-margin: 5px;

.page-content {
  height: auto;
  padding-bottom: 10px;

  .a4 & {
    max-height: $a4-height - $page-vertical-padding * 2;
  }

  .a5 & {
    max-height: $a5-height - $page-vertical-padding * 2;
  }

  ::v-deep {
    p {
      font-size: $proceedings-content-p-font-size;
      line-height: $proceedings-content-p-line-height;
      letter-spacing: $proceedings-content-p-letter-spacing;
      transform: $proceedings-content-p-transform;
      word-break: $proceedings-content-p-word-break;
      font-weight: $proceedings-content-p-font-weight;
      color: $primary-font-color;
      margin-bottom: $proceedings-content-p-bottom-margin;
    }

    strong {
      font-weight: 900 !important;
      text-shadow: 0 0 0.5px currentColor;
    }

    em {
      font-style: italic;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      color: $primary-font-color;
      border: none !important;
      border-bottom: none !important;
    }

    ol,
    ul {
      li {
        font-size: 15px;
        line-height: 21px;
        letter-spacing: -0.03em;
        transform: scaleX(0.98);
        margin-left: 5px;
        padding-left: 3px;
        word-break: break-all;
      }
    }

    // 인용문 스타일
    blockquote, .blockquote, .doc-blockquote {
      margin: 12px 0;
      padding: 10px 14px;
      background: #f9fafb;
      border-left: 3px solid #9ca3af;
      font-size: 13px;
      line-height: 19px;
      color: #4b5563;
      text-indent: 0;
    }

    // table-basic table-header-colored 스타일
    table {
      width: auto;
      max-width: 100%;
      border-collapse: collapse;
      border-spacing: 0;
      border: 1px solid #ddd;
      font-size: 13px;
      letter-spacing: -0.03em;
      transform: scaleX(0.98);

      caption {
        padding: 12px;
        caption-side: top;
        color: #333;
        text-align: center;
        font-size: 13px;
        font-weight: 600;
        letter-spacing: 0.5px;
        border: none !important;
        background: none !important;
      }

      thead th {
        padding: 12px 16px;
        border: 1px solid #ddd;
        text-align: left;
        vertical-align: middle;
        background-color: #f5f5f5;
        color: #333;
        font-weight: 600;
      }

      tbody th,
      tfoot th {
        padding: 12px 16px;
        border: 1px solid #ddd;
        text-align: left;
        vertical-align: middle;
        font-weight: normal;
      }

      td {
        padding: 12px 16px;
        border: 1px solid #ddd;
        text-align: left;
        vertical-align: middle;
        word-break: break-word;
        white-space: normal;
      }

      th {
        padding: 12px 16px;
        border: 1px solid #ddd;
        text-align: left;
        vertical-align: middle;
      }
    }

    // 페이지 나누기 스타일
    .page-break {
      page-break-after: always;
      break-after: page;
      height: 1px;
      margin: 30px 0;
      border-bottom: 1px dashed #ccc;
    }

    // 참고문헌 스타일 - 래퍼 없이 개별 스타일
    .references-title {
      margin-top: 40px;
      font-size: 14px;
      font-weight: 700;
      margin-bottom: 16px;
    }

    .reference-item {
      margin: 6px 0;
      padding-left: 2em;
      text-indent: -2em;
      font-size: 12px;
      line-height: 1.6;
      page-break-inside: avoid;
      break-inside: avoid;
    }
  }
}

/** ****************************************************************************************
 * [ 논문 주석 스타일 ]
 *******************************************************************************************/
$proceedings-comment-width: 80px;

$proceedings-comment-font-size: 9.33px;
$proceedings-comment-font-weight: 300;
$proceedings-comment-line-height: 13.33px;
$proceedings-comment-break-word: break-word;

$proceedings-comment-vertical-margin: 10px;
$proceedings-comment-horizontal-margin: 30px;

$proceedings-comments-top-margin: 200px;
$proceedings-comments-bottom-margin: 100px;

// 주석 공통 스타일
.page-comments {
  position: absolute;
  top: $proceedings-comments-top-margin;
  bottom: $proceedings-comments-bottom-margin;
  width: $proceedings-comment-width;
  // overflow-y: auto;
}

.page-comment {
  margin-bottom: $proceedings-comment-vertical-margin;
  margin-top: $proceedings-comment-vertical-margin;

  font-family: $dotum-font-family;
  font-weight: $proceedings-comment-font-weight;
  font-size: $proceedings-comment-font-size;
  color: $primary-font-color;
  line-height: $proceedings-comment-line-height;
  text-align: justify;
}

// 왼쪽 주석
.page-comments.left {
  left: $proceedings-comment-horizontal-margin;
}

// 오른쪽 주석
.page-comments.right {
  right: $proceedings-comment-horizontal-margin;
}
</style>
